/**
 * ============================================================================
 * MULTIKEYS SCREEN LAYOUT GEOMETRY HELPER
 * File: src/components/configurator/geometry.ts
 *
 * This file contains pure mathematical functions used to calculate screen 
 * placements on the configurator canvas. It is fully decoupled from the DOM 
 * (Document Object Model) and browser-specific events, allowing it to be 
 * easily tested and integrated.
 *
 * COORDINDATE SYSTEM CONTRACT:
 * - 2D Cartesian Coordinate Space: 
 *   - The origin point (0, 0) is situated at the TOP-LEFT corner of the canvas.
 *   - The X-axis increases horizontally to the RIGHT.
 *   - The Y-axis increases vertically DOWNWARDS.
 * - Units: All positions and sizes are represented in logical CSS Pixels (px).
 * - Target alignment: This coordinate space maps directly to the virtual space
 *   used by the background Rust service to determine when a mouse cursor crosses
 *   from the edge of one display to another. Keep coordinate representation
 *   precise to prevent boundary mismatch / cursor jumps.
 * ============================================================================
 */

/**
 * Rect interface represents a 2D rectangular boundary in the layout.
 * It is used for both screen tiles and the canvas boundary itself.
 * 
 * To adjust display sizes or default canvas sizes:
 * - A standard 1080p monitor is: { width: 1920, height: 1080 }
 * - A standard 4K monitor is: { width: 3840, height: 2160 }
 */
export interface Rect {
  /** The X-coordinate of the rectangle's top-left corner (in CSS pixels) */
  x: number;
  /** The Y-coordinate of the rectangle's top-left corner (in CSS pixels) */
  y: number;
  /** The width of the rectangle (in CSS pixels) */
  width: number;
  /** The height of the rectangle (in CSS pixels) */
  height: number;
}

/**
 * Point interface represents a single coordinate pair (x, y) in 2D space.
 */
export interface Point {
  /** The X-coordinate */
  x: number;
  /** The Y-coordinate */
  y: number;
}

/**
 * applyDrag: Translates a screen's position by adding a drag displacement (dx, dy).
 *
 * HOW IT WORKS:
 * - We take the original screen rectangle and compute its new position by adding
 *   the delta displacement value to its current x and y values.
 * - This function is PURE: it returns a brand-new object instead of modifying 
 *   (mutating) the input screen object. This prevents state bugs in React.
 *
 * @param screen The original screen rectangle { x, y, width, height }
 * @param dx The distance moved horizontally (positive moves right, negative moves left)
 * @param dy The distance moved vertically (positive moves down, negative moves up)
 * @returns A new Rect representing the screen at its updated position
 */
export function applyDrag(screen: Rect, dx: number, dy: number): Rect {
  return {
    x: screen.x + dx,
    y: screen.y + dy,
    width: screen.width,
    height: screen.height,
  };
}

/**
 * snapToGrid: Snaps a 2D coordinate point to the nearest grid intersection.
 *
 * HOW IT WORKS:
 * - We divide the coordinate by the grid size, round to the nearest whole integer
 *   using Math.round, and then multiply back by the grid size.
 * - For example, if x = 23 and gridSize = 10:
 *     1. 23 / 10 = 2.3
 *     2. Math.round(2.3) = 2
 *     3. 2 * 10 = 20 (snapped value)
 * - If gridSize is set to 0 or a negative number, snapping is disabled and we
 *   return the original coordinates unchanged.
 * - JavaScript Math.round can produce `-0` values (e.g. for `Math.round(-0.1) * 10`).
 *   To guarantee consistency in downstream comparisons and database inserts,
 *   we convert `-0` values back to standard positive `0` using a quick ternary check.
 *
 * HOW TO TWEAK SETTINGS:
 * - Increase gridSize (e.g., to 20 or 50) for coarser, more pronounced snapping.
 * - Decrease gridSize (e.g., to 5 or 2) for finer, more precise movement.
 *
 * @param pos The starting point coordinate { x, y }
 * @param gridSize The snap grid increment in pixels (e.g., 10px or 20px)
 * @returns A new Point coordinate snapped to the grid (guaranteed no -0 coordinates)
 */
export function snapToGrid(pos: Point, gridSize: number): Point {
  // Guard clause: if grid size is invalid or zero, disable snapping
  if (gridSize <= 0) {
    return {
      x: pos.x,
      y: pos.y,
    };
  }
  
  // To prevent floating-point division inaccuracies (e.g., 0.15 / 0.1 returning 1.4999999999999998
  // which Math.round would incorrectly round down to 1), we scale the division quotient by 10^12 (1e12),
  // round it to the nearest whole integer, and then divide back by 10^12 before performing the final
  // grid-alignment rounding.
  //
  // Non-coder explanation: JavaScript sometimes has trouble with decimals (like 0.1 + 0.2 being 0.30000000000000004).
  // This nested Math.round logic cleans up those microscopic decimal errors by zooming in (multiplying by a trillion),
  // rounding, and then zooming back out (dividing by a trillion).
  //
  // How to tweak settings:
  // - The multiplier 1e12 controls the decimal precision (12 places). Do not lower it below 1e6 or you might lose
  //   legitimate sub-pixel movement accuracy. Keep it at 1e12 for maximum IEEE 754 float safety.
  // - Adjust gridSize in the parent config to make snapping chunkier (e.g., 20 or 50) or finer (e.g., 2 or 5).
  const rawX = Math.round(Math.round((pos.x / gridSize) * 1e12) / 1e12) * gridSize;
  const rawY = Math.round(Math.round((pos.y / gridSize) * 1e12) / 1e12) * gridSize;

  return {
    // If raw coordinate is negative zero, replace it with positive zero
    x: rawX === 0 ? 0 : rawX,
    y: rawY === 0 ? 0 : rawY,
  };
}

/**
 * clampToBounds: Constraints a screen rectangle's position to keep it entirely
 * within the canvas boundary.
 *
 * HOW IT WORKS:
 * - A screen cannot move past the left edge (x must be >= canvas.x).
 * - A screen cannot move past the right edge (the right edge of the screen, 
 *   which is x + screen.width, must be <= canvas.x + canvas.width. Rewritten as
 *   x <= canvas.x + canvas.width - screen.width).
 * - The same logic applies to the vertical boundaries (y-axis).
 * - If the screen is wider or taller than the canvas itself, the maximum limit
 *   would become smaller than the minimum limit. To handle this gracefully:
 *   we use Math.max(canvas.x, ...) to ensure the maximum allowed x does not fall
 *   below the left boundary. In such cases, the screen will align to the top-left
 *   of the canvas.
 *
 * @param rect The rectangle we want to clamp (the screen tile)
 * @param canvas The bounding rectangle defining the allowed canvas area
 * @returns A new Rect representing the screen clamped within canvas bounds
 */
export function clampToBounds(rect: Rect, canvas: Rect): Rect {
  // Calculate the horizontal bounds.
  // The screen's x coordinate must be between:
  //   Minimum: canvas.x
  //   Maximum: canvas.x + canvas.width - rect.width
  const minX = canvas.x;
  const maxX = Math.max(canvas.x, canvas.x + canvas.width - rect.width);
  const clampedX = Math.max(minX, Math.min(rect.x, maxX));

  // Calculate the vertical bounds.
  // The screen's y coordinate must be between:
  //   Minimum: canvas.y
  //   Maximum: canvas.y + canvas.height - rect.height
  const minY = canvas.y;
  const maxY = Math.max(canvas.y, canvas.y + canvas.height - rect.height);
  const clampedY = Math.max(minY, Math.min(rect.y, maxY));

  return {
    x: clampedX,
    y: clampedY,
    width: rect.width,
    height: rect.height,
  };
}

/**
 * screensOverlap: Checks if two screen rectangles overlap in 2D space.
 *
 * HOW IT WORKS:
 * - This function uses Axis-Aligned Bounding Box (AABB) intersection math.
 * - Two rectangles overlap if they intersect on both the X-axis and Y-axis.
 * - In screen layout arrangements, monitors are often placed adjacent to one 
 *   another, meaning they touch at the borders.
 *   - For example: Screen A has its right edge at x = 1920, and Screen B has its
 *     left edge at x = 1920.
 *   - By using strict inequalities (< and >) rather than soft inequalities (<= and >=),
 *     screens that touch edges or corners will NOT be considered overlapping.
 * - If either screen has a width or height that is zero or negative, we treat them
 *   as non-overlapping to avoid divide-by-zero or mathematical logical errors.
 *
 * @param a The first screen rectangle
 * @param b The second screen rectangle
 * @returns true if screen A and screen B overlap (intersect); false otherwise
 */
export function screensOverlap(a: Rect, b: Rect): boolean {
  // Guard check for invalid/degenerate rectangles (non-positive dimensions)
  if (a.width <= 0 || a.height <= 0 || b.width <= 0 || b.height <= 0) {
    return false;
  }

  // Two rectangles intersect if and only if all four conditions are met:
  // 1. The left edge of 'a' is to the left of the right edge of 'b'.
  // 2. The right edge of 'a' is to the right of the left edge of 'b'.
  // 3. The top edge of 'a' is above the bottom edge of 'b'.
  // 4. The bottom edge of 'a' is below the top edge of 'b'.
  return (
    a.x < b.x + b.width &&      // left edge of a < right edge of b
    a.x + a.width > b.x &&      // right edge of a > left edge of b
    a.y < b.y + b.height &&     // top edge of a < bottom edge of b
    a.y + a.height > b.y        // bottom edge of a > top edge of b
  );
}
